import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { MaintenanceTasksService } from '../Tenant/Applications/Airsweb/maintenance-tasks/maintenance-tasks.service';
import { ScheduledMaintenanceTask, ScheduledMaintenanceTaskDetails } from './scheduled-maintenance-task.model';
import { ScheduledMaintenanceTaskCreateRequest } from './scheduled-maintenance-task-create-request.model';
import { ScheduledMaintenanceSummary } from './scheduled-maintenance-summary.model';
import { ValidationResult } from '@app/Tenant/Applications/Airsweb/Services';
import {
    RecurringScheduledMaintenanceTaskMaterializeRequest,
    RecurringScheduledMaintenanceTaskOccurrencePreview,
    RecurringScheduledMaintenanceTaskSeriesCreateRequest,
    RecurringScheduledMaintenanceTaskSeriesDetails,
    RecurringScheduledMaintenanceTaskSeriesSummary
} from './scheduled-maintenance-recurrence.model';


@Injectable({
    providedIn: 'root'
})
export class MaintenanceTaskSchedulerDataService {
    private migrationApiUrl = 'api/ScheduledMaintenanceTasks';
    private readonly options = { headers: {
            'Content-Type' : 'application/json'
        }
    };

    constructor(
        private http: HttpClient,
        private tasksService: MaintenanceTasksService
    ) { }

    public getMigrationTasks(): Observable<ScheduledMaintenanceTask[]> {
        return this.http.get<ScheduledMaintenanceTask[]>(
            this.migrationApiUrl);
    }

    public createScheduledMaintenanceTask(taskTypeName: string,
        request: ScheduledMaintenanceTaskCreateRequest): Observable<ScheduledMaintenanceTaskCreateRequest> {

        const url = `${this.migrationApiUrl}/${taskTypeName}/Schedules`;
        return this.http.post<ScheduledMaintenanceTaskCreateRequest>(url, request);
    }

    public previewRecurringScheduledMaintenanceTask(taskTypeName: string,
        request: RecurringScheduledMaintenanceTaskSeriesCreateRequest): Observable<RecurringScheduledMaintenanceTaskOccurrencePreview[]> {

        const url = `${this.migrationApiUrl}/${taskTypeName}/RecurringSchedules/Preview`;
        return this.http.post<RecurringScheduledMaintenanceTaskOccurrencePreview[]>(url, this.serializeRecurringScheduleRequest(request));
    }

    public createRecurringScheduledMaintenanceTask(taskTypeName: string,
        request: RecurringScheduledMaintenanceTaskSeriesCreateRequest): Observable<number> {

        const url = `${this.migrationApiUrl}/${taskTypeName}/RecurringSchedules`;
        return this.http.post<number>(url, this.serializeRecurringScheduleRequest(request));
    }

    private serializeRecurringScheduleRequest(request: RecurringScheduledMaintenanceTaskSeriesCreateRequest): any {
        return {
            ...request,
            FirstLocalStartDateTime: this.formatLocalDateTime(request.FirstLocalStartDateTime),
            EndLocalDateTime: request.EndLocalDateTime ? this.formatLocalDateTime(request.EndLocalDateTime) : undefined
        };
    }

    private formatLocalDateTime(dateTime: Date): string {
        const pad = (value: number): string => value.toString().padStart(2, '0');

        return `${dateTime.getFullYear()}-${pad(dateTime.getMonth() + 1)}-${pad(dateTime.getDate())}` +
            `T${pad(dateTime.getHours())}:${pad(dateTime.getMinutes())}:${pad(dateTime.getSeconds())}`;
    }

    public materializeRecurringScheduledMaintenanceTasks(
        request: RecurringScheduledMaintenanceTaskMaterializeRequest): Observable<number> {

        return this.http.post<number>(`${this.migrationApiUrl}/RecurringSchedules/Materialize`, request);
    }

    public getRecurringScheduledMaintenanceTaskSeries(): Observable<RecurringScheduledMaintenanceTaskSeriesSummary[]> {
        return this.http.get<RecurringScheduledMaintenanceTaskSeriesSummary[]>(`${this.migrationApiUrl}/RecurringSchedules`);
    }

    public getRecurringScheduledMaintenanceTaskSeriesDetails(
        seriesId: number): Observable<RecurringScheduledMaintenanceTaskSeriesDetails> {

        return this.http.get<RecurringScheduledMaintenanceTaskSeriesDetails>(`${this.migrationApiUrl}/RecurringSchedules/${seriesId}`);
    }

    public pauseRecurringScheduledMaintenanceTaskSeries(seriesId: number): Observable<void> {
        return this.http.post<void>(`${this.migrationApiUrl}/RecurringSchedules/${seriesId}/Pause`, undefined);
    }

    public resumeRecurringScheduledMaintenanceTaskSeries(seriesId: number): Observable<void> {
        return this.http.post<void>(`${this.migrationApiUrl}/RecurringSchedules/${seriesId}/Resume`, undefined);
    }

    public cancelRecurringScheduledMaintenanceTaskSeries(seriesId: number): Observable<void> {
        return this.http.post<void>(`${this.migrationApiUrl}/RecurringSchedules/${seriesId}/Cancel`, undefined);
    }

    public getAllSchedules(): Observable<ScheduledMaintenanceSummary[]> {
        return this.http.get<ScheduledMaintenanceSummary[]>(`${this.migrationApiUrl}/Schedules`);
    }

    public getSchedule(taskTypeName: string, taskId: number): Observable<ScheduledMaintenanceTaskDetails> {
        return this.http.get<ScheduledMaintenanceTaskDetails>(`${this.migrationApiUrl}/${taskTypeName}/Schedules/${taskId}`);
    }

    public cancelScheduledMaintenanceTask(taskTypeName: string, taskId: number): Observable<ValidationResult> {
        const url = `${this.migrationApiUrl}/${taskTypeName}/Schedules/${taskId}/Cancel`;
        return this.http.post<ValidationResult>(url, undefined);
    }
    }
